import { useState, useCallback, useRef, useEffect } from 'react';
import {
  useMapLoader,
  useMapInteractions,
  useMapBounds,
  useMapZoom,
  useMapDirections,
  useSingleLocationEdit,
  useMapConfig,
} from '@hero/web-hooks';
import { LocationMode, MapLocationData } from '@hero/web-components';
import type { GoogleMapWithMarkerProps } from '@hero/web-components';
import { MapContextValue } from '@hero/web-context';
import { useAddressApi } from '@hero/api-hooks';
import { useMapLocations } from './useMapLocations';
import { useMapChangeTracking } from './useMapChangeTracking';
import { useMapCurrentLocation } from './useMapCurrentLocation';
import { useMapAutoFit } from './useMapAutoFit';
import { useMapNotifications } from './useMapNotifications';
import { useMapConfirmationHandlers } from './useMapConfirmationHandlers';
import { useMapMarkers } from './useMapMarkers';

/**
 * Main hook that manages all map state and logic for GoogleMapWithMarker
 * Composes smaller focused hooks to provide complete map functionality
 * Returns a MapContextValue that can be provided via MapProvider
 * Does not pass API callables; sub-hooks and components use api-hooks at use site.
 */
export function useMapState(props: GoogleMapWithMarkerProps): MapContextValue {
  const {
    apiKey,
    searchMode,
    locations: initialLocations = [],
    onLocationChange,
    onLocationsReady,
    onLocationsFinalized,
    onCancel,
    onLocationEditStart,
    onLocationEditEnd,
    getMarkerConfig,
    showFitAllButton = true,
    enableConfirmLocationsButton = false,
    saveInProgress = false,
    savePreparing = false,
    savePreparingReason,
    clientConfig,
  } = props;

  const addressApi = useAddressApi();

  // Extract all configuration values using useMapConfig hook
  const config = useMapConfig({
    locationsConfig: props.locationsConfig,
    mapConfig: props.mapConfig,
    clientConfig: props.clientConfig,
  });

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [locationMode, setLocationMode] = useState<LocationMode>(config.initialLocationMode);
  const mapSearchRef = useRef<{
    handleCoordinates: (lat: number, lng: number, index: number) => Promise<void>;
    isClosingDropdownRef: React.MutableRefObject<boolean>;
  }>(null);
  // Ref to store wrapped handleLocationEdit for useMapLocations auto-enter callback
  const wrappedHandleLocationEditRef = useRef<
    ((index: number, isOrigin: boolean) => Promise<void>) | null
  >(null);

  // Load Google Maps API
  const { isLoaded, loadError } = useMapLoader(apiKey);

  // Map bounds
  const { fitBounds } = useMapBounds({
    padding: config.boundsPadding,
    maxZoom: config.maxZoom,
    minZoom: config.minZoom,
  });

  // Map zoom
  const { zoomToLocation, smoothZoomToLocation } = useMapZoom({ zoom: 15 });

  // Locations state and handlers (call first to get locations and editingIndex)
  const locationsState = useMapLocations({
    initialLocations,
    locationMode,
    markerCountLimit: config.markerCountLimitValue || (locationMode === 'single' ? 1 : undefined),
    mode: config.mode,
    map,
    isLoaded,
    smoothZoomToLocation,
    fitBounds,
    onLocationChange,
    onLocationEditStart,
    onLocationEditEnd,
    enterEditModeInitially: config.enterEditModeInitially,
    orgId: clientConfig?.orgId ? String(clientConfig.orgId) : undefined,
    onAutoEnterEditMode: (index: number) => {
      // Call the wrapped handler via ref
      if (wrappedHandleLocationEditRef.current) {
        wrappedHandleLocationEditRef.current(index, true);
      }
    },
  });

  const {
    locations,
    setLocations,
    editingIndex,
    addLocation,
    enterEditMode,
    exitEditMode,
    handleLocationSelect,
    handleCardClick,
    handleAccurateLocationToggle,
    handleLocationEdit,
    handleLocationCancel,
    handleLocationDone,
    originalLocationRef,
    handleSaveLocationRequested,
    handleEditSavedLocationRequested,
    handleLocationSaved,
    isLocationModalOpen,
    locationModalCoords,
    locationModalIndex,
    locationModalSavedLocationId,
    locationModalLocationUrl,
    closeLocationModal,
    orgId: locationsOrgId,
    isSaveLocationAvailable,
  } = locationsState;

  // Map interactions - uses locations state (useMapInteractions calls useAddressApi internally)
  const {
    onMapClick: handleMapClickFromHook,
    onMarkerDragEnd: onMarkerDragEndRaw,
    isUpdatingAddress,
  } = useMapInteractions({
    onLocationChange: (location, index) => {
      const newLocations = [...locations];
      newLocations[index] = location;
      setLocations(newLocations);

      // Optional: call onLocationChange for real-time updates if provided
      if (onLocationChange) {
        onLocationChange(location, index);
      }
    },
    enableMapClick: config.mode !== 'view', // Allow map clicks in edit modes, not in view
    enableMarkerDrag: config.mode === 'add_new_location' || config.mode === 'edit', // Allow dragging in edit modes
  });

  // Wrap onMarkerDragEnd to return void for useMapMarkers
  const onMarkerDragEnd: (
    event: google.maps.MapMouseEvent,
    index: number,
    currentLocations: MapLocationData[],
  ) => Promise<void> = useCallback(
    async (
      event: google.maps.MapMouseEvent,
      index: number,
      currentLocations: MapLocationData[],
    ) => {
      await onMarkerDragEndRaw(event, index, currentLocations);
    },
    [onMarkerDragEndRaw],
  );

  // Single location editing mode (depends on locations and editingIndex)
  const singleLocationEdit = useSingleLocationEdit({
    editingIndex,
    locations,
    onLocationChange: (location, index) => {
      const newLocations = [...locations];
      newLocations[index] = location;
      setLocations(newLocations);
      // Optional: call onLocationChange for real-time updates if provided
      if (onLocationChange) {
        onLocationChange(location, index);
      }
    },
    map,
  });

  // Change tracking
  const changeTracking = useMapChangeTracking({
    locations,
    initialLocations,
    enableConfirmLocationsButton,
  });

  const { hasUnsavedChanges, isLocationEdited, updateConfirmedLocations } = changeTracking;

  // Current location management (useMapCurrentLocation calls useAddressApi internally)
  const currentLocationState = useMapCurrentLocation({
    showUserCurrentLocation: config.showUserCurrentLocation,
    isLoaded,
    map,
  });

  const { currentLocation, isGettingCurrentLocation, onCurrentLocationChange } =
    currentLocationState;

  // Auto-fit bounds
  useMapAutoFit({
    autoFitMarkerBounds: config.autoFitMarkerBounds,
    map,
    locations,
    fitBounds,
  });

  // Notifications
  useMapNotifications({
    editingIndex,
    locations,
    mode: config.mode,
    onLocationsReady,
    onLocationsFinalized,
    enableConfirmLocationsButton,
    updateConfirmedLocations,
  });

  // Confirmation handlers
  const confirmationHandlers = useMapConfirmationHandlers({
    locations,
    initialLocations,
    editingIndex,
    onLocationsReady,
    onCancel,
    setLocations,
    exitEditMode,
    getLastConfirmedLocations: () => changeTracking.lastConfirmedLocationsRef.current,
    updateConfirmedLocations,
  });

  const { handleSaveChanges, handleCancel } = confirmationHandlers;

  // Map directions (only initialize when map is loaded)
  const {
    directions,
    directionsConfigs,
    calculateDirections,
    isCalculating,
    clearDirections,
    recalculateDirections,
    directionsKey,
  } = useMapDirections(
    isLoaded && typeof google !== 'undefined' && google.maps && google.maps.TravelMode
      ? {
          travelMode: google.maps.TravelMode.DRIVING,
          strokeColor: '#3532A7',
          strokeWeight: 4,
          strokeOpacity: 0.8,
          routeLineType: config.routeLineType,
          editingIndex,
          locations,
          map,
          autoRecalculate: true,
        }
      : {
          routeLineType: config.routeLineType,
          editingIndex,
          locations,
          map,
          autoRecalculate: true,
        },
  );

  // Markers generation
  const markers = useMapMarkers({
    locations,
    editingIndex,
    mode: config.mode,
    getMarkerConfig,
    onMarkerDragEnd,
    isLoaded,
  });

  // Wrap handleLocationEdit to handle default location when none exists
  const wrappedHandleLocationEdit = useCallback(
    async (index: number, isOrigin: boolean) => {
      // Clear directions immediately when entering edit mode
      if (clearDirections) {
        clearDirections();
      }

      // Check if location exists at this index
      if (!locations[index]) {
        // No location exists - create default location
        let defaultCoords: { lat: number; lng: number };

        // Priority 1: Use current user location if available
        if (currentLocation?.cords) {
          defaultCoords = currentLocation.cords;
        }
        // Priority 2: Use map center if available
        else if (map) {
          const center = map.getCenter();
          if (center) {
            defaultCoords = { lat: center.lat(), lng: center.lng() };
          } else {
            // Fallback to default center (Riyadh)
            defaultCoords = { lat: 24.7136, lng: 46.6753 };
          }
        } else {
          // Fallback to default center (Riyadh)
          defaultCoords = { lat: 24.7136, lng: 46.6753 };
        }

        // Create default location
        const defaultLocation: MapLocationData = {
          cords: defaultCoords,
          isLoadingAddress: true,
          isAccurateLocation: false,
        };

        // Add location to array using functional update
        setLocations(prev => {
          if (locationMode === 'single') {
            return [defaultLocation];
          } else {
            // For multi mode, we need to add at the correct index
            const newLocations = [...prev];
            // Fill gaps if needed
            while (newLocations.length <= index) {
              newLocations.push({
                cords: { lat: 0, lng: 0 },
                isLoadingAddress: false,
                isAccurateLocation: false,
              });
            }
            newLocations[index] = defaultLocation;
            return newLocations;
          }
        });

        // Fetch address for default location (use addressApi from api-hooks)
        try {
          const addressData = await addressApi.mutateAsync({
            lat: defaultCoords.lat,
            lng: defaultCoords.lng,
          });
          const updatedLocation: MapLocationData = {
            ...defaultLocation,
            address: addressData,
            fullAddress: addressData.fullAddress,
            city: addressData.city?.name,
            isLoadingAddress: false,
          };

          // Update location with address using functional update
          setLocations(prev => {
            if (locationMode === 'single') {
              return [updatedLocation];
            } else {
              const newLocations = [...prev];
              while (newLocations.length <= index) {
                newLocations.push({
                  cords: { lat: 0, lng: 0 },
                  isLoadingAddress: false,
                  isAccurateLocation: false,
                });
              }
              newLocations[index] = updatedLocation;
              return newLocations;
            }
          });

          // Notify parent of location change
          if (onLocationChange) {
            onLocationChange(updatedLocation, index);
          }
        } catch (error) {
          console.error('Failed to fetch address for default location:', error);
          // Still proceed with location without address
          const updatedLocation: MapLocationData = {
            ...defaultLocation,
            isLoadingAddress: false,
          };
          setLocations(prev => {
            if (locationMode === 'single') {
              return [updatedLocation];
            } else {
              const newLocations = [...prev];
              while (newLocations.length <= index) {
                newLocations.push({
                  cords: { lat: 0, lng: 0 },
                  isLoadingAddress: false,
                  isAccurateLocation: false,
                });
              }
              newLocations[index] = updatedLocation;
              return newLocations;
            }
          });
          if (onLocationChange) {
            onLocationChange(updatedLocation, index);
          }
        }

        // Wait a tick for React state to update, then enter edit mode
        // Use setTimeout to ensure state has updated
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      // Call the original handler (now location should exist)
      handleLocationEdit(index, isOrigin);
    },
    [
      handleLocationEdit,
      clearDirections,
      locations,
      locationMode,
      setLocations,
      currentLocation,
      map,
      addressApi,
      onLocationChange,
    ],
  );

  // Store the wrapped handler in ref so useMapLocations can access it
  useEffect(() => {
    wrappedHandleLocationEditRef.current = wrappedHandleLocationEdit;
  }, [wrappedHandleLocationEdit]);

  // Wrap handleLocationDone and handleLocationCancel to include direction clearing/recalculating
  // These handlers need access to clearDirections and recalculateDirections from useMapDirections
  const wrappedHandleLocationDone = useCallback(
    (index: number, isOrigin: boolean) => {
      // Clear directions before exiting edit mode
      if (clearDirections) {
        clearDirections();
      }
      // Call the original handler
      handleLocationDone(index, isOrigin);
      // Recalculate directions after a delay to ensure old lines are removed
      if (recalculateDirections) {
        setTimeout(() => {
          recalculateDirections();
        }, 150);
      }
    },
    [handleLocationDone, clearDirections, recalculateDirections],
  );

  const wrappedHandleLocationCancel = useCallback(
    (index: number, isOrigin: boolean) => {
      // Clear directions before exiting edit mode
      if (clearDirections) {
        clearDirections();
      }
      // Call the original handler
      handleLocationCancel(index, isOrigin);
      // Recalculate directions after a delay to ensure old lines are removed
      if (recalculateDirections) {
        setTimeout(() => {
          recalculateDirections();
        }, 150);
      }
    },
    [handleLocationCancel, clearDirections, recalculateDirections],
  );

  // Search visibility - show search if:
  // 1. In 'add_new_location' mode (always show search)
  // 2. OR if editing a single location (editingIndex !== null)
  const showSearch =
    (searchMode !== 'no-search' && config.mode === 'add_new_location') ||
    (editingIndex !== null && config.mode === 'edit');

  // Handle map load
  const onMapLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  }, []);

  // Handle map click
  const handleMapClick = useCallback(
    async (event: google.maps.MapMouseEvent) => {
      // If in single location edit mode, use the edit handler
      if (singleLocationEdit.isSingleLocationEditMode) {
        // Check if dropdown is being closed
        if (mapSearchRef.current?.isClosingDropdownRef?.current) {
          return;
        }

        const handleCoordinates = mapSearchRef.current?.handleCoordinates;
        if (handleCoordinates) {
          singleLocationEdit.handleMapClickInEditMode(event, handleCoordinates);
        } else {
          console.warn('MapSearch ref not available for address fetching');
          singleLocationEdit.handleMapClickInEditMode(event);
        }
        return;
      }

      // Only allow adding new locations in 'add_new_location' mode
      if (config.mode !== 'add_new_location') {
        return;
      }

      // Normal map click behavior
      if (!event.latLng) return;

      const locationData = await handleMapClickFromHook(event);
      if (!locationData) return;

      if (locationMode === 'single') {
        setLocations([locationData]);
      } else {
        addLocation(locationData);
      }

      if (onLocationChange) {
        const newIndex = locationMode === 'single' ? 0 : locations.length;
        onLocationChange(locationData, newIndex);
      }
    },
    [
      singleLocationEdit,
      config.mode,
      handleMapClickFromHook,
      locationMode,
      addLocation,
      setLocations,
      locations,
      onLocationChange,
    ],
  );

  // Handle pending marker drag
  const handlePendingMarkerDrag = useCallback(
    async (lat: number, lng: number) => {
      if (isLoaded && typeof google !== 'undefined' && google.maps && editingIndex !== null) {
        const currentLocation = singleLocationEdit.editingLocation;
        if (!currentLocation) return;

        const updatedLocationImmediate = {
          ...currentLocation,
          cords: { lat, lng },
          isLoadingAddress: true,
          isAccurateLocation: false, // Any coordinate change during editing = inaccurate until confirmed
        };

        const newLocations = [...locations];
        newLocations[editingIndex] = updatedLocationImmediate;
        setLocations(newLocations);

        if (onLocationChange) {
          onLocationChange(updatedLocationImmediate, editingIndex);
        }

        if (map) {
          map.panTo({ lat, lng });
          const currentZoom = map.getZoom();
          if (typeof currentZoom === 'number' && currentZoom < 15) {
            map.setZoom(15);
          }
        }

        if (mapSearchRef.current) {
          await mapSearchRef.current.handleCoordinates(lat, lng, editingIndex);
        }
      }
    },
    [
      isLoaded,
      editingIndex,
      singleLocationEdit.editingLocation,
      locations,
      setLocations,
      map,
      onLocationChange,
    ],
  );

  return {
    // State
    map,
    locations,
    editingIndex,
    currentLocation,
    isLoaded,
    loadError: loadError || null,
    isGettingCurrentLocation,

    // Config
    mode: config.mode,
    locationMode,
    showUserCurrentLocation: config.showUserCurrentLocation,
    routeLineType: config.routeLineType,
    markerIcon: config.markerIcon,
    enableConfirmLocationsButton,
    saveInProgress,
    savePreparing,
    savePreparingReason,
    showFitAllButton,
    enableAccurateLocationIndicator: config.enableAccurateLocationIndicator,

    // Computed
    showSearch,
    markers,
    hasUnsavedChanges,
    isLocationEdited,
    updateConfirmedLocations,
    editingLocation: singleLocationEdit.editingLocation,

    // Handlers
    onMapLoad,
    handleMapClick,
    handleLocationSelect,
    handleCardClick,
    handleLocationEdit: wrappedHandleLocationEdit,
    handleLocationCancel: wrappedHandleLocationCancel,
    handleLocationDone: wrappedHandleLocationDone,
    handleAccurateLocationToggle,
    handleSaveChanges,
    handleCancel,
    handlePendingMarkerDrag,

    // Map utilities
    fitBounds,
    smoothZoomToLocation,

    // Directions
    directions,
    directionsKey,
    isCalculating,
    clearDirections,
    recalculateDirections,

    // Refs
    mapSearchRef: mapSearchRef as React.RefObject<{
      handleCoordinates: (lat: number, lng: number, index: number) => Promise<void>;
      isClosingDropdownRef: React.MutableRefObject<boolean>;
    }>,

    // Callbacks
    onCurrentLocationChange,

    // Save location (no API callables; LocationEditModal uses api-hooks internally)
    handleSaveLocationRequested,
    saveLocationState: {
      isLocationModalOpen,
      locationModalCoords,
      locationModalIndex,
      locationModalSavedLocationId,
      locationModalLocationUrl,
      handleSaveLocationRequested,
      handleEditSavedLocationRequested,
      handleLocationSaved,
      closeLocationModal,
      orgId: locationsOrgId,
      isSaveLocationAvailable: Boolean(isSaveLocationAvailable),
    } as MapContextValue['saveLocationState'],

    // Props (no API callables; MapSearch and inner components use api-hooks at use site)
    props,
  };
}
